import { useEffect, useState } from "react";
import { Link } from "@/lib/router-compat";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Plus, Eye, Loader2, Download, Brain } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { Skeleton } from "@/components/ui/skeleton";

interface ThreatRow {
  id: string;
  threat_title: string;
  client_site_name: string;
  risk_level: string;
  created_at: string;
  ai_generated_assessment: string | null;
  property_type: string;
  known_risks: string[] | null;
  recommended_mitigations: string | null;
  access_points: string | null;
  lighting_conditions: string | null;
  camera_coverage: string | null;
  incident_history: string | null;
  client_concerns: string | null;
  hours_of_operation: string | null;
  state: string | null;
  city: string | null;
}

const riskColors: Record<string, string> = {
  low: "bg-green-500/10 text-green-500 border-green-500/30",
  moderate: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30",
  high: "bg-orange-500/10 text-orange-500 border-orange-500/30",
  critical: "bg-destructive/10 text-destructive border-destructive/30",
};

const riskPdfColors: Record<string, { r: number; g: number; b: number }> = {
  low: { r: 34, g: 197, b: 94 },
  moderate: { r: 234, g: 179, b: 8 },
  high: { r: 249, g: 115, b: 22 },
  critical: { r: 239, g: 68, b: 68 },
};

const ThreatList = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [threats, setThreats] = useState<ThreatRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);

  const fetchThreats = async () => {
    setLoading(true);
    setLoadError(null);
    const { data, error } = await supabase
      .from("threat_assessments")
      .select("id, threat_title, client_site_name, risk_level, created_at, ai_generated_assessment, property_type, known_risks, recommended_mitigations, access_points, lighting_conditions, camera_coverage, incident_history, client_concerns, hours_of_operation, state")
      .order("created_at", { ascending: false });
    if (error) {
      setLoadError(error.message);
    } else if (data) {
      setThreats(data as ThreatRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    fetchThreats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleExportPDF = async (threat: ThreatRow) => {
    setExportingId(threat.id);
    toast({ title: "Exporting PDF", description: "Preparing your threat assessment..." });

    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF();
      const margin = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const contentWidth = pageWidth - margin * 2;
      let y = margin;

      const accentColor = { r: 30, g: 58, b: 95 };
      const riskColor = riskPdfColors[threat.risk_level] || accentColor;

      const checkPage = (needed: number) => {
        if (y + needed > 270) {
          doc.addPage();
          y = margin;
        }
      };

      const addSection = (title: string, content: string) => {
        if (!content) return;
        checkPage(20);
        doc.setFontSize(13);
        doc.setTextColor(accentColor.r, accentColor.g, accentColor.b);
        doc.text(title, margin, y);
        y += 2;
        doc.setDrawColor(accentColor.r, accentColor.g, accentColor.b);
        doc.setLineWidth(0.5);
        doc.line(margin, y, margin + contentWidth, y);
        y += 6;
        doc.setFontSize(10);
        doc.setTextColor(40, 40, 40);
        const lines = doc.splitTextToSize(content, contentWidth);
        for (const line of lines) {
          checkPage(6);
          doc.text(line, margin, y);
          y += 5;
        }
        y += 8;
      };

      // Title
      doc.setFontSize(22);
      doc.setTextColor(accentColor.r, accentColor.g, accentColor.b);
      doc.text("Threat Assessment Report", margin, y);
      y += 10;

      // Title underline
      doc.setDrawColor(accentColor.r, accentColor.g, accentColor.b);
      doc.setLineWidth(1);
      doc.line(margin, y, margin + contentWidth, y);
      y += 10;

      // Meta info
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      doc.text(`Assessment: ${threat.threat_title}`, margin, y); y += 6;
      doc.text(`Client / Site: ${threat.client_site_name}`, margin, y); y += 6;
      doc.text(`Property Type: ${threat.property_type.charAt(0).toUpperCase() + threat.property_type.slice(1)}`, margin, y); y += 6;
      if (threat.city) { doc.text(`City: ${threat.city}`, margin, y); y += 6; }
      if (threat.state) { doc.text(`State: ${threat.state}`, margin, y); y += 6; }
      doc.text(`Date: ${format(new Date(threat.created_at), "MMMM d, yyyy")}`, margin, y); y += 8;

      // Risk level badge
      doc.setFillColor(riskColor.r, riskColor.g, riskColor.b);
      doc.roundedRect(margin, y, 60, 10, 2, 2, "F");
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text(`Risk Level: ${threat.risk_level.toUpperCase()}`, margin + 4, y + 7);
      y += 18;

      // AI Assessment (primary content)
      if (threat.ai_generated_assessment) {
        addSection("AI-Generated Threat Assessment", threat.ai_generated_assessment);
      }

      // Manual data sections (if no AI or as supplement)
      if (threat.access_points) addSection("Access Points", threat.access_points);
      if (threat.lighting_conditions) addSection("Lighting Conditions", threat.lighting_conditions);
      if (threat.camera_coverage) addSection("Camera Coverage", threat.camera_coverage);
      if (threat.hours_of_operation) addSection("Hours of Operation", threat.hours_of_operation);
      if (threat.incident_history) addSection("Incident History", threat.incident_history);
      if (threat.known_risks?.length) addSection("Known Risks", threat.known_risks.join(", "));
      if (threat.client_concerns) addSection("Client Concerns", threat.client_concerns);
      if (threat.recommended_mitigations) addSection("Recommended Mitigations", threat.recommended_mitigations);

      // Footer
      const pageCount = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text("Generated by Sentinel Forge AI", margin, 287);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, 287, { align: "right" });
      }

      doc.save(`threat-assessment-${threat.threat_title.replace(/\s+/g, "-").toLowerCase()}-${Date.now()}.pdf`);
      toast({ title: "PDF Exported", description: "Threat assessment downloaded." });
    } catch (err: any) {
      console.error("PDF export error:", err);
      toast({ title: "Export Failed", description: "Could not generate PDF.", variant: "destructive" });
    } finally {
      setExportingId(null);
    }
  };

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Threat Assessments</h1>
            <p className="text-sm text-muted-foreground">All saved threat assessments for your account</p>
          </div>
          <Link to="/threat-builder">
            <Button className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
              <Plus className="mr-2 h-4 w-4" /> New Threat
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="space-y-3" aria-label="Loading threat assessments">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="border-border bg-card">
                <CardContent className="flex items-center justify-between py-4 gap-3">
                  <div className="flex-1 min-w-0 space-y-2">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-1/2" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-24" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : loadError ? (
          <Card className="border-border bg-card">
            <CardContent className="p-0">
              <ErrorState
                title="Couldn't load threat assessments"
                message={loadError}
                onRetry={fetchThreats}
                retrying={loading}
              />
            </CardContent>
          </Card>
        ) : threats.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="p-0">
              <EmptyState
                icon={AlertTriangle}
                title="No threat assessments yet"
                description="Document site vulnerabilities, generate AI-powered risk analysis, and export professional reports for your clients."
                action={
                  <Link to="/threat-builder">
                    <Button className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
                      <Plus className="mr-2 h-4 w-4" /> Create your first assessment
                    </Button>
                  </Link>
                }
              />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {threats.map((threat) => (
              <Card key={threat.id} className="border-border bg-card shadow-card hover:border-primary/30 transition-colors">
                <CardContent className="flex items-center justify-between py-4 gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-foreground truncate">{threat.threat_title}</p>
                      {threat.ai_generated_assessment && (
                        <Badge className="bg-primary/10 text-primary border-primary/30 text-xs shrink-0">
                          <Brain className="h-3 w-3 mr-1" /> AI
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{threat.client_site_name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(new Date(threat.created_at), "MMM d, yyyy")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    <Badge variant="outline" className={riskColors[threat.risk_level] || ""}>
                      {threat.risk_level.charAt(0).toUpperCase() + threat.risk_level.slice(1)}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExportPDF(threat)}
                      disabled={exportingId === threat.id}
                    >
                      {exportingId === threat.id ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <Download className="mr-1 h-3 w-3" />
                      )}
                      PDF
                    </Button>
                    <Link to={`/threat-builder/${threat.id}`}>
                      <Button variant="outline" size="sm">
                        <Eye className="mr-1 h-3 w-3" /> View / Edit
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ThreatList;
